const populateFeaturedProductTabLabels = (root = document) => {
  for (const panel of root.querySelectorAll('.featured-product-tabs__panel[data-tab-title]')) {
    const tab = Array.from(root.querySelectorAll('[role="tab"]')).find(
      (candidate) => candidate.getAttribute('aria-controls') === panel.id
    );

    if (tab && panel.dataset.tabTitle) tab.textContent = panel.dataset.tabTitle;
  }
};

const initializedFeaturedProductTabs = new WeakSet();

const setupMobileProgress = (panel) => {
  const progress = panel.querySelector('[data-featured-product-tabs-progress]');
  const slideshow = panel.querySelector('slideshow-component');
  const scroller = slideshow?.querySelector('slideshow-slides');
  const slides = scroller ? Array.from(scroller.querySelectorAll(':scope > slideshow-slide')) : [];

  if (!progress || !slideshow || !scroller || !slides.length) return () => {};

  const updateProgress = (selectedIndex) => {
    const index = Math.max(0, Math.min(selectedIndex, slides.length - 1));
    progress.style.width = `${((index + 1) / slides.length) * 100}%`;
  };

  const updateFromScroll = () => {
    const nextIndex = slides.reduce((closestIndex, slide, index) => (
      Math.abs(slide.offsetLeft - scroller.scrollLeft) < Math.abs(slides[closestIndex].offsetLeft - scroller.scrollLeft)
        ? index
        : closestIndex
    ), 0);
    updateProgress(nextIndex);
  };

  let scrollFrame;
  const onScroll = () => {
    cancelAnimationFrame(scrollFrame);
    scrollFrame = requestAnimationFrame(updateFromScroll);
  };
  const onSelect = (event) => updateProgress(event.detail.index);

  scroller.addEventListener('scroll', onScroll, { passive: true });
  slideshow.addEventListener('slideshow:select', onSelect);
  requestAnimationFrame(updateFromScroll);

  return updateFromScroll;
};

const setupFeaturedProductTabs = (root = document) => {
  for (const section of root.querySelectorAll('[data-testid="featured-product-tabs"]')) {
    if (initializedFeaturedProductTabs.has(section)) continue;
    initializedFeaturedProductTabs.add(section);

    const tabs = Array.from(section.querySelectorAll('.featured-product-tabs__tablist [role="tab"]'));
    const panels = Array.from(section.querySelectorAll('.featured-product-tabs__panel'));
    const updateProgress = panels.map((panel) => setupMobileProgress(panel));

    const selectTab = (selectedIndex) => {
      for (const [index, tab] of tabs.entries()) {
        const selected = index === selectedIndex;
        tab.setAttribute('aria-selected', String(selected));
        tab.tabIndex = selected ? 0 : -1;
      }

      for (const [index, panel] of panels.entries()) {
        const selected = index === selectedIndex;
        panel.toggleAttribute('inert', !selected);
        panel.tabIndex = selected ? 0 : -1;
        if (selected) requestAnimationFrame(updateProgress[index]);
      }
    };

    for (const [index, tab] of tabs.entries()) {
      tab.addEventListener('click', () => selectTab(index));
      tab.addEventListener('keydown', (event) => {
        const offset = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
        const targetIndex = event.key === 'Home' ? 0 : event.key === 'End' ? tabs.length - 1 : index + offset;
        if (offset === 0 && event.key !== 'Home' && event.key !== 'End') return;

        event.preventDefault();
        const nextIndex = (targetIndex + tabs.length) % tabs.length;
        selectTab(nextIndex);
        tabs[nextIndex].focus();
      });
    }

    const initiallySelected = tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
    selectTab(Math.max(initiallySelected, 0));
  }
};

populateFeaturedProductTabLabels();
setupFeaturedProductTabs();

document.addEventListener('shopify:section:load', (event) => {
  populateFeaturedProductTabLabels(event.target);
  setupFeaturedProductTabs(event.target);
});
